import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NavDot from "@/components/NavDot";
import { useAdminActionCounts, useEmployeeActionCounts } from "@/hooks/useActionCounts";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Users, Building2, BriefcaseBusiness, FileText,
  ShieldCheck, UserCog, ScrollText, LogOut, Menu, X, ShieldAlert, ClipboardCheck, CalendarDays, CalendarClock,
  MessageSquareWarning, MapPin, Clock, Bell, Wallet, Library, BookOpenCheck, Handshake, UserRound, FilePenLine, Plane
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/employees", label: "Employee Master", icon: Users },
  { to: "/departments", label: "Departments", icon: Building2 },
  { to: "/designations", label: "Designations & JD", icon: BriefcaseBusiness },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/letter-library", label: "Letter Library", icon: Library },
  { to: "/policies", label: "Policy Library", icon: BookOpenCheck },
  { to: "/issued-letters", label: "Letters for Acceptance", icon: FilePenLine },
  { to: "/payroll", label: "Payroll", icon: Wallet },
  { to: "/service-providers", label: "Projects & Providers", icon: Handshake },
  { to: "/consultants", label: "Consultant Fees", icon: Wallet },
  { to: "/leave", label: "Leave", icon: CalendarDays },
  { to: "/grievances", label: "Grievances", icon: MessageSquareWarning },
  { to: "/on-duty", label: "On Duty", icon: Plane },
  { to: "/holidays", label: "Holidays", icon: CalendarClock },
  { to: "/attendance-locations", label: "Attendance Locations", icon: MapPin },
  { to: "/attendance-today", label: "Attendance Today", icon: Clock },
  { to: "/weekly-off-roster", label: "Weekly Off Roster", icon: CalendarClock },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck },
  { to: "/users", label: "User Accounts", icon: UserCog, adminOnly: true },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout, isSuperAdmin, hasEmployeeProfile, employeeProfile, setViewMode } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const items = NAV.filter((n) => !n.adminOnly || isSuperAdmin);
  const counts = useAdminActionCounts();
  // The CEO's other hat: show a dot on "My Space" when something is waiting there.
  const myCounts = useEmployeeActionCounts(hasEmployeeProfile);
  const myTotal = Object.values(myCounts).reduce((a, b) => a + (Number(b) || 0), 0);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="App flex bg-background">
      {/* Sidebar */}
      <aside className={cn(
        "brand-sidebar fixed z-40 inset-y-0 left-0 w-64 flex flex-col transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )} data-testid="sidebar">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <img src="/intenciv-mark.webp" alt="IntenCiv" className="h-9 w-9 rounded-md object-contain bg-white/95 p-0.5" />
          <div className="leading-tight">
            <p className="text-white font-serif-display text-lg tracking-tight">IntenCiv</p>
            <p className="text-teal text-[10px] tracking-[0.2em] uppercase">Diagnostics HR</p>
          </div>
          <button className="ml-auto lg:hidden text-white/70" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => cn(
                "nav-link relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/75",
                isActive && "nav-link-active"
              )}
            >
              <n.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
              {n.label}
              <NavDot count={counts[n.to]} className="ml-auto" />
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-teal/20 grid place-items-center text-white text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-white/50 text-[11px] flex items-center gap-1">
                {isSuperAdmin ? <ShieldAlert className="h-3 w-3" /> : <UserCog className="h-3 w-3" />}
                {isSuperAdmin ? "Super Admin" : "HR Manager"}
              </p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/75 hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-navy" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-6 w-6" />
          </button>
          <img src="/intenciv-logo.webp" alt="IntenCiv Diagnostics" className="h-7 object-contain hidden sm:block" />
          <span className="ml-auto overline text-slate-400 hidden md:inline">ISO 15189:2022 · Personnel & Competence</span>
          {hasEmployeeProfile && (
            <Button size="sm" variant="outline" className="ml-auto md:ml-4 border-teal text-navy" data-testid="switch-to-employee"
              onClick={() => { setViewMode("employee"); navigate("/"); }}>
              <UserRound className="h-4 w-4 mr-1" /> My Space ({employeeProfile.employee_id}) <NavDot count={myTotal} className="ml-2" />
            </Button>
          )}
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto fade-in">{children}</main>
      </div>
    </div>
  );
}
